/**
 * ticketflow/adminSchema — descripteur `AdminSchema` du domaine TicketFlow.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * C'est LA partie spécifique (la « couture » d'extraction, D14) : le shell
 * (@mostajs/app-admin) est générique ; ce fichier décrit les sections/champs de
 * la config TicketFlow (arbre `Config.data`). Champs texte = trilingues AR/FR/EN
 * (D12). Les sections en tableau (services/guichets/publicité) = champ « repeater ».
 *
 * ⚠ PROVISOIRE (principe « ne pas réinventer — utiliser le serveur ») : ce schéma
 * DUPLIQUE la structure de config que le serveur possède déjà (`config-api/defaults.ts`
 * + contrat `@mostajs/queue`). CIBLE : le serveur EXPOSE le descripteur (dérivé du
 * contrat de domaine via `queue.toAdminSchema()`, D14/D3), et l'admin le RÉCUPÈRE
 * (`GET /api/config/schema`) au lieu de le coder en dur ici. Ce fichier tient lieu
 * de gabarit tant que l'endpoint n'existe pas.
 */
const L = (ar, fr, en) => ({ ar, fr, en });

export const ticketflowAdminSchema = {
  title: L('إدارة TicketFlow', 'Administration TicketFlow', 'TicketFlow Administration'),
  sections: [
    {
      id: 'agency',
      label: L('الوكالة', 'Agence', 'Agency'),
      roles: ['admin', 'manager'],
      fields: [
        { path: 'agency.name', type: 'text', label: L('اسم الوكالة', "Nom de l'agence", 'Agency name') },
        { path: 'agency.logo', type: 'text', label: L('الشعار (رابط)', 'Logo (URL)', 'Logo (URL)') },
        { path: 'ui.welcomeMessage', type: 'localized', label: L('رسالة الترحيب', 'Message de bienvenue', 'Welcome message') },
        { path: 'ui.ticketInstruction', type: 'localized', label: L('تعليمات التذكرة', 'Instruction ticket', 'Ticket instruction') },
      ],
    },
    {
      id: 'services',
      label: L('الخدمات', 'Services', 'Services'),
      roles: ['admin', 'manager'],
      fields: [
        {
          path: 'serviceTypes',
          type: 'repeater',
          label: L('أنواع الخدمات', 'Types de services', 'Service types'),
          addLabel: L('إضافة خدمة', 'Ajouter un service', 'Add service'),
          // Clés ALIGNÉES sur le modèle serveur (config-api/defaults.ts) : id/name/nameAr/active
          // — et NON code/enabled/localized (sinon champs vides + non échangés, finding 4.2).
          itemDefaults: { active: true },
          itemFields: [
            { key: 'id', type: 'text', label: L('المعرّف', 'Identifiant (code)', 'Id (code)') },
            { key: 'name', type: 'text', label: L('الاسم (فرنسي)', 'Nom (FR)', 'Name (FR)') },
            { key: 'nameAr', type: 'text', label: L('الاسم (عربي)', 'Nom (AR)', 'Name (AR)') },
            { key: 'nameEn', type: 'text', label: L('الاسم (إنجليزي)', 'Nom (EN)', 'Name (EN)') },
            { key: 'active', type: 'boolean', label: L('مفعّل', 'Actif', 'Active') },
          ],
        },
      ],
    },
    {
      id: 'counters',
      label: L('الشبابيك', 'Guichets', 'Counters'),
      roles: ['admin', 'manager'],
      fields: [
        {
          path: 'counters',
          type: 'repeater',
          label: L('الشبابيك', 'Guichets', 'Counters'),
          addLabel: L('إضافة شباك', 'Ajouter un guichet', 'Add counter'),
          // Clés ALIGNÉES sur le serveur : id/name/nameAr/serviceType/active. Défaut `active:true`
          // + `status:'available'` (et NON 'closed') pour qu'un guichet ajouté ne parte pas HORS
          //  LIGNE (finding 4.3). `id` numérique pour matcher le store runtime (finding 4.3/2.1).
          itemDefaults: { active: true, status: 'available' },
          itemFields: [
            { key: 'id', type: 'number', label: L('المعرّف', 'Identifiant', 'Id') },
            { key: 'name', type: 'text', label: L('الاسم (فرنسي)', 'Nom (FR)', 'Name (FR)') },
            { key: 'nameAr', type: 'text', label: L('الاسم (عربي)', 'Nom (AR)', 'Name (AR)') },
            { key: 'nameEn', type: 'text', label: L('الاسم (إنجليزي)', 'Nom (EN)', 'Name (EN)') },
            { key: 'serviceType', type: 'text', label: L('نوع الخدمة (رمز)', 'Service (code)', 'Service (code)') },
            { key: 'active', type: 'boolean', label: L('مفعّل', 'Actif', 'Active') },
          ],
        },
      ],
    },
    {
      id: 'appearance',
      label: L('المظهر', 'Apparence', 'Appearance'),
      roles: ['admin', 'manager'],
      fields: [
        { path: 'ui.primaryColor', type: 'color', label: L('اللون الأساسي', 'Couleur primaire', 'Primary color') },
        { path: 'ui.secondaryColor', type: 'color', label: L('اللون الثانوي', 'Couleur secondaire', 'Secondary color') },
      ],
    },
    {
      id: 'behavior',
      label: L('سلوك الطابور', "Comportement de la file", 'Queue behavior'),
      roles: ['admin'],
      fields: [
        { path: 'server.ticketLifetime', type: 'number', label: L('مدة صلاحية التذكرة (دقائق)', 'Durée de vie du ticket (min)', 'Ticket lifetime (min)') },
        { path: 'server.resetTime', type: 'text', label: L('وقت إعادة التعيين', 'Heure de reset (HH:MM)', 'Reset time (HH:MM)') },
      ],
    },
    {
      // Numérotation des tickets (config poussée au distributeur — comme le son/voix).
      // Édité ici (console centrale) ET sur /manage (onglet Impression) : même config serveur.
      id: 'numbering',
      label: L('التذكرة / الطباعة', 'Ticket / Impression', 'Ticket / Printing'),
      roles: ['admin'],
      fields: [
        {
          path: 'ticketNumbering.mode',
          type: 'select',
          label: L('عرض الرقم', 'Affichage du numéro', 'Number display'),
          options: [
            { value: 'service-only', label: L('رقم الخدمة فقط', 'Service seul (ex. C-007)', 'Service only') },
            { value: 'global-only', label: L('الرقم العام فقط', "Ordre global seul (ex. 042)", 'Global only') },
            { value: 'global-plus-service', label: L('كلاهما', 'Les deux (global + service)', 'Both') },
          ],
        },
        {
          path: 'ticketNumbering.showWaitingAhead',
          type: 'boolean',
          label: L('طباعة "~N أمامك"', 'Imprimer « ~N personnes avant vous »', 'Print "~N ahead of you"'),
        },
        {
          path: 'ticketNumbering.waitingScope',
          type: 'select',
          label: L('نطاق الموضع', 'Portée de la position', 'Position scope'),
          options: [
            { value: 'service', label: L('حسب الخدمة', 'Par service', 'Per service') },
            { value: 'global', label: L('إجمالي', 'Global (toutes files)', 'Global') },
          ],
        },
        // Tailles des éléments imprimés (1 = 100 %) : sur papier thermique étroit, un
        // numéro trop grand déborde et se retrouve rogné en haut du ticket.
        { path: 'ticketNumbering.scale.number', type: 'number', label: L('حجم رقم الخدمة (نسبة)', 'Taille du n° de service (échelle, 1 = 100 %)', 'Service number size (scale)') },
        { path: 'ticketNumbering.scale.globalTag', type: 'number', label: L('حجم رقم الترتيب العام (نسبة)', 'Taille du n° d\'ordre global (échelle)', 'Global order number size (scale)') },
        { path: 'ticketNumbering.scale.position', type: 'number', label: L('حجم سطر "أمامك" (نسبة)', 'Taille de la ligne « avant vous » (échelle)', '"Ahead of you" line size (scale)') },
        // Visibilité, élément par élément, des trois nombres imprimés.
        { path: 'ticketNumbering.show.globalTag', type: 'boolean', label: L('إظهار رقم الترتيب العام', 'Afficher le n° d\'ordre global', 'Show global order number') },
        { path: 'ticketNumbering.show.number', type: 'boolean', label: L('إظهار رقم الخدمة', 'Afficher le n° de service (ex. RET13)', 'Show service number') },
        { path: 'ticketNumbering.show.position', type: 'boolean', label: L('إظهار "أمامك N"', 'Afficher « ~N personnes avant vous »', 'Show "~N ahead of you"') },
        { path: 'ticketNumbering.show.name', type: 'boolean', label: L('إظهار اسم الخدمة', 'Afficher le nom du service', 'Show service name') },
        // N'a de sens que si le n° d'ordre existe : quand seul lui est imprimé, c'est
        // même le SEUL moyen d'appeler l'usager au guichet.
        { path: 'ticketNumbering.callByGlobalAtCounter', type: 'boolean', label: L('السماح بالنداء برقم الترتيب في الشباك', 'Autoriser l\'appel par n° d\'ordre au guichet', 'Allow calling by order number at the counter') },
        // Présentation du ticket IMPRIMÉ — pilotée ici, plus borne par borne : le rendu doit
        // être identique sur tout un site. Le matériel (imprimante, papier, copies, son)
        // reste réglé localement sur chaque distributeur.
        {
          path: 'ticketPrint.template',
          type: 'select',
          label: L('نموذج التذكرة', 'Modèle de ticket', 'Ticket template'),
          options: [
            { value: 'standard', label: L('قياسي', 'Standard', 'Standard') },
            { value: 'compact', label: L('مضغوط', 'Compact (sans logo ni pied de page)', 'Compact') },
            { value: 'premium', label: L('فاخر', 'Premium', 'Premium') },
            { value: 'minimal', label: L('مبسّط', 'Minimal', 'Minimal') },
            { value: 'administrative', label: L('إداري', 'Administratif', 'Administrative') },
          ],
        },
        { path: 'ticketPrint.showBarcode', type: 'boolean', label: L('طباعة الباركود', 'Imprimer le code-barres', 'Print barcode') },
        { path: 'ticketPrint.showQRCode', type: 'boolean', label: L('طباعة رمز QR', 'Imprimer le QR code', 'Print QR code') },
        { path: 'ticketPrint.showLogo', type: 'boolean', label: L('طباعة الشعار', 'Imprimer le logo', 'Print logo') },
        // Écarte le premier élément du ticket de la zone non imprimable de l'imprimante
        // thermique, où il disparaissait sans laisser de trace. 0 = comportement d'origine.
        { path: 'ticketPrint.marginTop', type: 'number', label: L('الهامش العلوي للتذكرة (مم)', 'Marge haute du ticket (mm)', 'Ticket top margin (mm)') },
      ],
    },
    {
      // Menu de /manage (serveur) : masquer une par une les tuiles du menu d'administration.
      id: 'manageMenu',
      label: L('قائمة الإدارة', 'Menu /manage', 'Manage menu'),
      roles: ['admin'],
      fields: [
        { path: 'manageMenu.audit', type: 'boolean', label: L('إخفاء "التدقيق"', 'Masquer « Audit »', 'Hide "Audit"') },
        { path: 'manageMenu.centralConfig', type: 'boolean', label: L('إخفاء "الإعداد"', 'Masquer « Config »', 'Hide "Config"') },
        { path: 'manageMenu.sound', type: 'boolean', label: L('إخفاء "الصوت"', 'Masquer « Son & Annonces »', 'Hide "Sound"') },
        { path: 'manageMenu.numbering', type: 'boolean', label: L('إخفاء "الترقيم"', 'Masquer « Numérotation »', 'Hide "Numbering"') },
        { path: 'manageMenu.displays', type: 'boolean', label: L('إخفاء "الشاشات"', 'Masquer « Affichages »', 'Hide "Displays"') },
        { path: 'manageMenu.backup', type: 'boolean', label: L('إخفاء "النسخ الاحتياطي"', 'Masquer « Sauvegardes »', 'Hide "Backups"') },
      ],
    },
    {
      // Styles de /display : taille de base + échelle PAR LANGUE (ordre AR/EN/FR).
      id: 'displayStyles',
      label: L('أنماط العرض', 'Styles affichage', 'Display styles'),
      roles: ['admin'],
      fields: [
        { path: 'styles.display.serviceNameSize', type: 'text', label: L('حجم اسم الخدمة (مثال 1.8rem)', 'Taille du nom de service (ex. 1.8rem)', 'Service name size (e.g. 1.8rem)') },
        { path: 'styles.display.serviceNameOrderAr', type: 'number', label: L('ترتيب العربية (1 = الأول)', 'Ordre AR (1 = premier)', 'AR order (1 = first)') },
        { path: 'styles.display.showServiceNameAr', type: 'boolean', label: L('إظهار العربية', 'Afficher AR', 'Show AR') },
        { path: 'styles.display.serviceNameScaleAr', type: 'number', label: L('حجم العربية (نسبة، 1 = 100٪)', 'Taille AR (échelle, 1 = 100%)', 'AR scale (1 = 100%)') },
        { path: 'styles.display.serviceNameOrderEn', type: 'number', label: L('ترتيب الإنجليزية', 'Ordre EN', 'EN order') },
        { path: 'styles.display.showServiceNameEn', type: 'boolean', label: L('إظهار الإنجليزية', 'Afficher EN', 'Show EN') },
        { path: 'styles.display.serviceNameScaleEn', type: 'number', label: L('حجم الإنجليزية (نسبة)', 'Taille EN (échelle)', 'EN scale') },
        { path: 'styles.display.serviceNameOrderFr', type: 'number', label: L('ترتيب الفرنسية', 'Ordre FR', 'FR order') },
        { path: 'styles.display.showServiceNameFr', type: 'boolean', label: L('إظهار الفرنسية', 'Afficher FR', 'Show FR') },
        { path: 'styles.display.serviceNameScaleFr', type: 'number', label: L('حجم الفرنسية (نسبة)', 'Taille FR (échelle)', 'FR scale') },
        // Distributeur (borne) : un seul champ. Noms de service, forme des cadres et grille
        // suivent les champs « affichage » ci-dessus — un réglage pour les deux écrans publics.
        { path: 'styles.distributor.serviceButtonColor', type: 'color', label: L('لون مربع الخدمة (الموزّع)', 'Couleur des cadres de service (distributeur)', 'Service card colour (distributor)') },
      ],
    },
  ],
};
